import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useSubscription } from "@/contexts/SubscriptionContext";

export interface UserTest {
  id: string;
  title: string;
  description: string;
  created_at: string;
  status: string;
  location: string;
  response_count?: number;
  is_active?: boolean;
  isCollaborated?: boolean;
  collaborationPermission?: string;
}

export function useUserTests() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { refreshSubscription } = useSubscription();

  const { data: tests, isLoading, error } = useQuery({
    queryKey: ['user-tests', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      console.log('Fetching tests for user:', user.id);

      try {
        // Fetch owned tests
        const { data: ownedTests, error: ownedError } = await supabase
          .from('tests')
          .select(`
            id,
            title,
            description,
            created_at,
            status,
            location,
            is_active
          `)
          .eq('creator_id', user.id)
          .order('created_at', { ascending: false });

        if (ownedError) {
          console.error('Error fetching owned tests:', ownedError);
          throw ownedError;
        }

        // Fetch collaborated tests using a simpler approach
        const { data: collaborations, error: collabError } = await supabase
          .from('test_collaborators')
          .select('test_id, permission')
          .eq('collaborator_id', user.id)
          .eq('status', 'accepted');

        if (collabError) {
          console.error('Error fetching collaborations:', collabError);
          // Don't throw here, just continue with owned tests
        }

        let collaboratedTests: any[] = [];
        
        // If we have collaborations, fetch the test details separately
        if (collaborations && collaborations.length > 0) {
          const testIds = collaborations.map(c => c.test_id);
          
          const { data: collaboratedTestData, error: testError } = await supabase
            .from('tests')
            .select(`
              id,
              title,
              description,
              created_at,
              status,
              location,
              is_active
            `)
            .in('id', testIds)
            .order('created_at', { ascending: false });

          if (testError) {
            console.error('Error fetching collaborated test details:', testError);
          } else if (collaboratedTestData) {
            // Map the collaborated tests with their permissions
            collaboratedTests = collaboratedTestData.map(test => {
              const collaboration = collaborations.find(c => c.test_id === test.id);
              return {
                ...test,
                isCollaborated: true,
                collaborationPermission: collaboration?.permission
              };
            });
          }
        }

        // Combine both lists
        const allTests = [
          ...(ownedTests || []).map(test => ({ ...test, isCollaborated: false })),
          ...collaboratedTests
        ];

        // Sort by created_at descending
        allTests.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        console.log('Found tests:', {
          owned: ownedTests?.length || 0,
          collaborated: collaboratedTests.length,
          total: allTests.length
        });

        // If we have no tests, return an empty array
        if (allTests.length === 0) {
          return [];
        }

        // Fetch response counts for each test
        const testsWithCounts = await Promise.all(
          allTests.map(async (test) => {
            try {
              const { data: responseData, error: countError } = await supabase
                .from('test_responses')
                .select('id')
                .eq('test_id', test.id)
                .in('status', ['completed', 'in_progress']);

              if (countError) {
                console.warn('Error getting response count for test:', test.id, countError);
                return {
                  ...test,
                  response_count: 0
                };
              }

              return {
                ...test,
                response_count: responseData?.length || 0
              };
            } catch (countErr) {
              console.warn('Error in response count calculation for test:', test.id, countErr);
              return {
                ...test,
                response_count: 0
              };
            }
          })
        );

        console.log('Successfully fetched tests with counts:', testsWithCounts);
        return testsWithCounts as UserTest[];
      } catch (error: any) {
        console.error('Error in useUserTests query:', error);
        
        toast({
          title: "Couldn't load tests",
          description: error.message || "Please try again later",
          variant: "destructive"
        });
        
        return [];
      }
    },
    enabled: !!user,
    staleTime: 30000,
    refetchOnMount: true,
  });

  // Function to delete a test and update the cache
  const deleteTest = async (testId: string) => {
    if (!user?.id) throw new Error("User not authenticated");
    
    try {
      console.log(`Deleting test with ID: ${testId}`);
      
      const { error } = await supabase
        .from('tests')
        .delete()
        .eq('id', testId)
        .eq('creator_id', user.id); // Security check
      
      if (error) {
        console.error('Error deleting test:', error);
        throw error;
      }
      
      console.log('Test deleted successfully');
      
      // Update the cache by removing the deleted test
      queryClient.setQueryData(['user-tests', user.id], (oldData: UserTest[] | undefined) => {
        if (!oldData) return [];
        return oldData.filter(test => test.id !== testId);
      });
      
      // Also invalidate test details cache if it exists
      queryClient.invalidateQueries({ queryKey: ['test', testId] });

      // Refresh subscription data to update active tests count
      await refreshSubscription();
      
      return true;
    } catch (error: any) {
      console.error('Error deleting test:', error);
      throw error;
    }
  };

  const refreshTests = () => {
    if (user?.id) {
      queryClient.invalidateQueries({ queryKey: ['user-tests', user.id] });
    }
  };

  return {
    tests: tests || [],
    isLoading,
    error,
    deleteTest,
    refreshTests
  };
}
